import { createFileRoute, Link } from "@tanstack/react-router";
import { PageHero } from "@/components/page-hero";
import heroImg from "@/assets/dj-mariage-equipment.jpg";
import { trackEvent } from "@/lib/analytics";
import { metaDesc } from "@/lib/seo-description";

export const Route = createFileRoute("/politique-confidentialite")({
  head: () => ({
    meta: [
      { title: "Politique de confidentialité | DJ Val" },
      { name: "description", content: metaDesc("Politique de confidentialité DJ Val : comment vos données personnelles sont collectées, utilisées, conservées et protégées, conformément au RGPD.") },
      { name: "robots", content: "index,follow" },
      { property: "og:title", content: "Politique de confidentialité — DJ Val" },
      { property: "og:url", content: "https://dj-val.fr/politique-confidentialite" },
    ],
    links: [{ rel: "canonical", href: "https://dj-val.fr/politique-confidentialite" }],
  }),
  component: Page,
});

function Page() {
  return (
    <div className="bg-background text-muted-foreground">
      <PageHero
        image={heroImg}
        eyebrow="Vos données, votre confiance"
        title={<>Politique de <span className="italic">confidentialité</span></>}
        subtitle="Comment DJ Val collecte, utilise et protège vos données personnelles dans le respect du RGPD."
        height="compact"
      />
      <section className="py-16 px-6 max-w-3xl mx-auto">
        <div className="space-y-8 text-sm leading-relaxed font-light">

          <p className="text-foreground/90">
            DJ Val (« nous ») s'engage à protéger la vie privée des visiteurs et clients du site dj-val.fr,
            conformément au Règlement Général sur la Protection des Données (RGPD) et à la loi Informatique et Libertés.
          </p>

          <div>
            <h2 className="font-serif-display text-xl text-foreground mb-3">1. Données collectées</h2>
            <p>Nous collectons uniquement les données que vous nous transmettez volontairement via :</p>
            <ul className="mt-3 space-y-2 list-disc list-inside marker:text-[var(--gold)]">
              <li>Le formulaire de devis (nom, prénom, e-mail, téléphone, date, lieu, type d'événement, message)</li>
              <li>Les e-mails que vous nous envoyez directement</li>
              <li>Les appels téléphoniques (aucun enregistrement)</li>
            </ul>
          </div>

          <div>
            <h2 className="font-serif-display text-xl text-foreground mb-3">2. Finalités</h2>
            <ul className="space-y-2 list-disc list-inside marker:text-[var(--gold)]">
              <li>Répondre à votre demande de devis sous 24 heures</li>
              <li>Gérer la relation contractuelle (devis, facture, suivi de l'événement)</li>
              <li>Conserver l'historique de prestation à des fins de garantie et de comptabilité</li>
            </ul>
            <p className="mt-3">
              Nous ne faisons <strong className="text-foreground">jamais</strong> de publicité commerciale par e-mail,
              ne transmettons aucune donnée à des tiers à des fins marketing et ne revendons aucune information.
            </p>
          </div>

          <div>
            <h2 className="font-serif-display text-xl text-foreground mb-3">3. Durée de conservation</h2>
            <p>
              Les données liées à un devis non concrétisé sont conservées 12 mois. Les données liées à une prestation
              effectuée sont conservées 10 ans (obligation comptable et fiscale).
            </p>
          </div>

          <div>
            <h2 className="font-serif-display text-xl text-foreground mb-3">4. Vos droits</h2>
            <p>
              Vous disposez à tout moment d'un droit d'accès, de rectification, d'effacement, de portabilité et
              d'opposition. Exercez-les en écrivant à{" "}
              <a href="mailto:info.djval@gmail.com" onClick={() => trackEvent("contact_click", { contact_method: "email", cta_location: "politique_confidentialite" })} className="text-[var(--gold)] hover:underline">info.djval@gmail.com</a> —
              réponse sous 30 jours maximum.
            </p>
            <p className="mt-3">
              En cas de litige, vous pouvez saisir la CNIL :{" "}
              <a href="https://www.cnil.fr" onClick={() => trackEvent("outbound_click", { link_url: "https://www.cnil.fr", cta_location: "politique_confidentialite" })} target="_blank" rel="noopener noreferrer" className="text-[var(--gold)] hover:underline">
                www.cnil.fr
              </a>
            </p>
          </div>

          <div>
            <h2 className="font-serif-display text-xl text-foreground mb-3">5. Cookies</h2>
            <p>
              Le site dj-val.fr n'utilise <strong className="text-foreground">aucun cookie publicitaire ni traceur tiers</strong>.
              Seuls des cookies techniques strictement nécessaires au fonctionnement du site peuvent être utilisés.
            </p>
            <p className="mt-3">
              Si vous utilisez le sélecteur de langue, vous serez redirigé vers le service Google Translate, soumis à
              sa propre politique de confidentialité.
            </p>
          </div>

          <div>
            <h2 className="font-serif-display text-xl text-foreground mb-3">6. Sécurité</h2>
            <p>
              Le site est servi en HTTPS (chiffrement TLS). Les données du formulaire sont transmises chiffrées et ne
              sont accessibles qu'à DJ Val.
            </p>
          </div>

          <p className="text-xs text-muted-foreground pt-6 border-t border-foreground/10">
            Dernière mise à jour : juin 2026 · Voir aussi nos{" "}
            <Link to="/mentions-legales" className="text-[var(--gold)] hover:underline">mentions légales</Link> et nos{" "}
            <Link to="/conditions-de-location" className="text-[var(--gold)] hover:underline">conditions de location</Link>.
          </p>
        </div>
      </section>
    </div>
  );
}
